import {
    getScorerRubricBandForScore,
    SPROUT_AGENT,
    SPROUT_JUDGE_SCORER_ID,
} from '@fruitful/llm-service-shared';
import { useRouter } from 'expo-router';
import { useMemo, useState } from 'react';
import {
    BadgeLabel,
    type BadgeLabelVariant,
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    H5Emphasized,
    H6Emphasized,
    LabelRegular,
    MonoBodySmallRegular,
    ScrollView,
    Spinner,
    SpinnerIcon,
    XStack,
    YStack,
} from 'smoothie';

import { formatToSimpleDateTimeString } from '../../common/utils/formatting';
import {
    ExperimentAgentDropdown,
    SPROUT_EXPERIMENT_AGENTS,
} from './ExperimentAgentDropdown';
import {
    type ExperimentDetail,
    type ExperimentResult,
    type ExperimentScore,
    type ExperimentSummary,
} from './loadExperiments';
import {
    rubricBracketLabel,
    rubricCountColor,
    tallyJudgeRubric,
    UNSCORED_BRACKET_KEY,
} from './rubricBracketTally';
import { RubricSummary } from './RubricSummary';
import { useDeleteExperiment } from './useExperimentActions';
import {
    useExperiment,
    useExperimentRubricScores,
    useExperiments,
} from './useExperiments';

const statusLabel = (status: ExperimentSummary['status']): string => {
    switch (status) {
        case 'pending':
            return 'Pending';
        case 'running':
            return 'Running';
        case 'completed':
            return 'Completed';
        case 'failed':
            return 'Failed';
    }
};

const statusVariant = (
    status: ExperimentSummary['status'],
): BadgeLabelVariant => {
    switch (status) {
        case 'completed':
            return 'positive';
        case 'running':
            return 'blue';
        case 'failed':
            return 'danger';
        case 'pending':
            return 'neutral';
    }
};

const countsLabel = (run: ExperimentSummary): string => {
    const ran = `${String(run.succeededCount)} of ${String(run.totalItems)} ran`;
    const failed =
        run.failedCount > 0
            ? ` · ${String(run.failedCount)} failed to execute`
            : '';
    const skipped =
        run.skippedCount > 0 ? ` · ${String(run.skippedCount)} skipped` : '';
    return `${ran}${failed}${skipped}`;
};

const versionLabel = (version: number | null): string | null =>
    version == null ? null : `Dataset version ${String(version)}`;

const PageMessage = ({ children }: { children: string }) => (
    <YStack
        backgroundColor="white"
        borderColor="$gray200"
        borderWidth={1}
        borderRadius={20}
        padding={20}
    >
        <BodyMediumRegular>{children}</BodyMediumRegular>
    </YStack>
);

const RubricCount = ({
    count,
    label,
    color,
}: {
    count: number;
    label: string;
    color: string;
}) => (
    <XStack
        alignItems="center"
        gap={6}
        paddingHorizontal={8}
        paddingVertical={4}
        borderRadius={8}
        backgroundColor="$gray100"
    >
        <BodySmallRegular color={color}>{String(count)}</BodySmallRegular>
        <LabelRegular color="$gray1000">{label}</LabelRegular>
    </XStack>
);

const RubricLoading = () => (
    <XStack alignItems="center" gap={8}>
        <SpinnerIcon size={16} color="$gray1000" />
        <LabelRegular color="$gray1000">Loading scores</LabelRegular>
    </XStack>
);

const RunRubricStrip = ({
    scores,
    totalItems,
}: {
    scores: readonly number[];
    totalItems: number;
}) => {
    const brackets = tallyJudgeRubric(scores);
    const unscored = Math.max(totalItems - scores.length, 0);

    return (
        <XStack gap={6} flexWrap="wrap">
            {brackets.map((band) => (
                <RubricCount
                    key={rubricBracketLabel(band)}
                    count={band.count}
                    label={rubricBracketLabel(band)}
                    color={rubricCountColor(band)}
                />
            ))}
            <RubricCount
                count={unscored}
                label="Not scored"
                color={unscored === 0 ? '$gray1000' : '$gray1200'}
            />
        </XStack>
    );
};

const RunRow = ({
    agentId,
    run,
    scores,
    isLoadingScores,
}: {
    agentId: string;
    run: ExperimentSummary;
    scores: readonly number[] | null;
    isLoadingScores: boolean;
}) => {
    const router = useRouter();
    const version = versionLabel(run.datasetVersion);
    const when = formatToSimpleDateTimeString(run.startedAt);

    return (
        <YStack
            backgroundColor="white"
            borderColor="$gray400"
            borderWidth={1}
            borderRadius={16}
            padding={16}
            gap={8}
            cursor="pointer"
            hoverStyle={{ backgroundColor: '$gray100' }}
            onPress={() => {
                router.push({
                    pathname: '/dataset/[agentId]/experiments/[experimentId]',
                    params: { agentId, experimentId: run.id },
                });
            }}
        >
            <XStack justifyContent="space-between" alignItems="center" gap={12}>
                <H6Emphasized flexShrink={1}>{run.name}</H6Emphasized>
                <BadgeLabel
                    variant={statusVariant(run.status)}
                    size="regular"
                    text={statusLabel(run.status)}
                />
            </XStack>
            <BodySmallRegular color="$gray1000">
                {[when, version]
                    .filter((part) => part != null && part !== '')
                    .join(' · ')}
            </BodySmallRegular>
            <BodySmallRegular>{countsLabel(run)}</BodySmallRegular>
            {scores != null ? (
                <RunRubricStrip scores={scores} totalItems={run.totalItems} />
            ) : isLoadingScores ? (
                <RubricLoading />
            ) : null}
        </YStack>
    );
};

const LoadingCard = () => (
    <YStack
        backgroundColor="white"
        borderColor="$gray200"
        borderWidth={1}
        borderRadius={20}
        padding={32}
    >
        <Spinner />
    </YStack>
);

const ExperimentActions = ({
    agentId,
    experimentId,
    onDeleted,
}: {
    agentId: string;
    experimentId: string;
    onDeleted?: () => void;
}) => {
    const { mutate: remove, isPending: deleting } =
        useDeleteExperiment(agentId);
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    return (
        <Button
            variant={ButtonVariant.SECONDARY}
            tone="danger"
            size={ButtonSize.SMALL}
            width="auto"
            text={confirmingDelete ? 'Confirm delete' : 'Delete'}
            disabled={deleting}
            onPress={() => {
                if (!confirmingDelete) {
                    setConfirmingDelete(true);
                    return;
                }

                remove(experimentId, {
                    onSuccess: () => {
                        onDeleted?.();
                    },
                });
            }}
        />
    );
};

const RunStartingNotice = ({
    agentId,
    experiment,
}: {
    agentId: string;
    experiment: ExperimentSummary;
}) => (
    <XStack
        backgroundColor="white"
        borderColor="$gray200"
        borderWidth={1}
        borderRadius={20}
        padding={16}
        gap={12}
        alignItems="center"
        flexWrap="wrap"
    >
        <SpinnerIcon size={20} color="$green900" />
        <YStack flex={1} minWidth={0} gap={4}>
            <BodyMediumEmphasized>Experiment in progress</BodyMediumEmphasized>
            <BodySmallRegular color="$gray1000">
                {countsLabel(experiment)}
            </BodySmallRegular>
        </YStack>
        <ExperimentActions agentId={agentId} experimentId={experiment.id} />
    </XStack>
);

export const ExperimentsPage = ({ agentId }: { agentId: string }) => {
    const router = useRouter();
    const [agentKey, setAgentKey] = useState<string>(SPROUT_AGENT.agentKey);
    const selectedAgent =
        SPROUT_EXPERIMENT_AGENTS.find((agent) => agent.agentKey === agentKey) ??
        SPROUT_AGENT;
    const { runs, isLoading, isError } = useExperiments(agentId);
    const visibleRuns = useMemo(
        () => runs.filter((run) => run.targetId === selectedAgent.id),
        [runs, selectedAgent.id],
    );
    const rubric = useExperimentRubricScores(agentId, visibleRuns);
    const listedInProgress = visibleRuns.find(
        (run) => run.status === 'running' || run.status === 'pending',
    );

    return (
        <YStack
            flex={1}
            minHeight={0}
            width="100%"
            height="100%"
            padding={16}
            gap={16}
        >
            <YStack
                backgroundColor="white"
                borderColor="$gray200"
                borderWidth={1}
                borderRadius={20}
                padding={20}
            >
                <XStack
                    justifyContent="space-between"
                    alignItems="center"
                    gap={16}
                >
                    <YStack gap={4} flex={1}>
                        <H5Emphasized>Experiments</H5Emphasized>
                        <BodySmallRegular color="$gray1000">
                            {selectedAgent.agentKey}
                        </BodySmallRegular>
                    </YStack>
                    <XStack gap={8} alignItems="center">
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            width="auto"
                            text="Dataset"
                            onPress={() => {
                                router.push({
                                    pathname: '/dataset/[agentId]',
                                    params: { agentId },
                                });
                            }}
                        />
                        <ExperimentAgentDropdown
                            agentKey={selectedAgent.agentKey}
                            onAgentKeyChange={setAgentKey}
                        />
                    </XStack>
                </XStack>
            </YStack>
            {listedInProgress != null ? (
                <RunStartingNotice
                    agentId={agentId}
                    experiment={listedInProgress}
                />
            ) : null}
            {isLoading ? <LoadingCard /> : null}
            {isError ? (
                <PageMessage>Unable to load experiments.</PageMessage>
            ) : null}
            {!isLoading && !isError && visibleRuns.length === 0 ? (
                <PageMessage>
                    {`No experiments for ${selectedAgent.agentKey} yet. Start one from the dataset page.`}
                </PageMessage>
            ) : null}
            {!isLoading && !isError && visibleRuns.length > 0 ? (
                <ScrollView flex={1}>
                    <YStack gap={12} paddingBottom={24}>
                        {rubric.isError ? (
                            <PageMessage>
                                Unable to load the rubric summary.
                            </PageMessage>
                        ) : null}
                        {visibleRuns.map((run) => (
                            <RunRow
                                key={run.id}
                                agentId={agentId}
                                run={run}
                                scores={
                                    rubric.scoresByRunId.get(run.id) ?? null
                                }
                                isLoadingScores={rubric.loadingRunIds.has(
                                    run.id,
                                )}
                            />
                        ))}
                    </YStack>
                </ScrollView>
            ) : null}
        </YStack>
    );
};

const scoreColor = (score: number | null): string => {
    if (score == null) {
        return '$gray1000';
    }
    const band = getScorerRubricBandForScore(SPROUT_JUDGE_SCORER_ID, score);
    if (band == null) {
        return '$gray1200';
    }
    if (band.minPercent >= 70) {
        return '$green1100';
    }
    if (band.minPercent >= 40) {
        return '$orange1100';
    }
    return '$red1100';
};

const scoreLabel = (score: ExperimentScore): string => {
    if (score.error != null && score.error !== '') {
        return score.error;
    }
    if (score.score == null) {
        return 'No score';
    }
    const percent = Math.round(score.score * 100);
    const band = getScorerRubricBandForScore(score.scorerId, score.score);
    return band == null
        ? `${String(percent)}%`
        : `${String(percent)}% · ${band.summary}`;
};

const judgeScore = (scores: ExperimentScore[]): ExperimentScore | null =>
    scores.find((score) => score.scorerId === SPROUT_JUDGE_SCORER_ID) ??
    scores[0] ??
    null;

const judgeScoreValues = (results: readonly ExperimentResult[]): number[] =>
    results.flatMap((result) => {
        const score = judgeScore(result.scores);
        return score?.score == null ? [] : [score.score];
    });

const bracketKeyForResult = (result: ExperimentResult): string => {
    const score = judgeScore(result.scores);
    if (score?.score == null) {
        return UNSCORED_BRACKET_KEY;
    }
    const band = getScorerRubricBandForScore(
        SPROUT_JUDGE_SCORER_ID,
        score.score,
    );
    return band == null ? UNSCORED_BRACKET_KEY : rubricBracketLabel(band);
};

interface RunResultGroup {
    name: string;
    results: ExperimentResult[];
}

const groupRunResults = (
    results: readonly ExperimentResult[],
    groupOrder: readonly string[],
): RunResultGroup[] => {
    const byName = new Map<string, ExperimentResult[]>();
    for (const result of results) {
        const name = result.group === '' ? 'Ungrouped' : result.group;
        const group = byName.get(name) ?? [];
        group.push(result);
        byName.set(name, group);
    }

    const names = [...groupOrder];
    for (const name of byName.keys()) {
        if (!names.includes(name)) {
            names.push(name);
        }
    }

    return names.flatMap((name) => {
        const group = byName.get(name);
        return group == null || group.length === 0
            ? []
            : [{ name, results: group }];
    });
};

const caseCountLabel = (count: number): string =>
    count === 1 ? '1 case' : `${String(count)} cases`;

const ResultCard = ({
    result,
    isFirst,
}: {
    result: ExperimentResult;
    isFirst: boolean;
}) => {
    const score = judgeScore(result.scores);
    const detail =
        result.extraTurnCount > 0
            ? result.extraTurnCount === 1
                ? '1 follow-up'
                : `${String(result.extraTurnCount)} follow-ups`
            : null;

    return (
        <YStack
            gap={10}
            padding={16}
            borderTopWidth={isFirst ? 0 : 1}
            borderTopColor="$gray200"
        >
            <XStack gap={16} alignItems="flex-start">
                <YStack flex={1} minWidth={0} gap={4}>
                    <BodyMediumEmphasized>
                        {result.prompt !== '' ? result.prompt : result.caseId}
                    </BodyMediumEmphasized>
                    {detail != null ? (
                        <BodySmallRegular color="$gray1000">
                            {detail}
                        </BodySmallRegular>
                    ) : null}
                </YStack>
                <YStack
                    flexShrink={0}
                    maxWidth="40%"
                    alignItems="flex-end"
                    gap={4}
                >
                    <MonoBodySmallRegular color="$gray1000" textAlign="right">
                        {result.caseId}
                    </MonoBodySmallRegular>
                    {score != null ? (
                        <BodySmallRegular color={scoreColor(score.score)}>
                            {scoreLabel(score)}
                        </BodySmallRegular>
                    ) : null}
                </YStack>
            </XStack>
            {result.error != null && result.error !== '' ? (
                <YStack gap={4}>
                    <LabelRegular color="$red1100">Error</LabelRegular>
                    <BodySmallRegular color="$red1100">
                        {result.error}
                    </BodySmallRegular>
                </YStack>
            ) : null}
            {score?.reason != null && score.reason !== '' ? (
                <YStack gap={4}>
                    <LabelRegular color="$gray1000">Judge</LabelRegular>
                    <BodySmallRegular>{score.reason}</BodySmallRegular>
                </YStack>
            ) : null}
            {result.groundTruth !== '' ? (
                <YStack
                    gap={4}
                    padding={12}
                    borderRadius={12}
                    backgroundColor="$gray100"
                >
                    <LabelRegular color="$gray1000">Expected</LabelRegular>
                    <BodySmallRegular>{result.groundTruth}</BodySmallRegular>
                </YStack>
            ) : null}
            {result.output !== '' ? (
                <YStack
                    gap={4}
                    padding={12}
                    borderRadius={12}
                    backgroundColor="$gray100"
                >
                    <LabelRegular color="$gray1000">Answer</LabelRegular>
                    <BodySmallRegular>{result.output}</BodySmallRegular>
                </YStack>
            ) : null}
        </YStack>
    );
};

const RunGroup = ({ group }: { group: RunResultGroup }) => (
    <YStack gap={10}>
        <XStack alignItems="center" gap={10}>
            <YStack
                width={4}
                height={22}
                borderRadius={2}
                backgroundColor="$gray1200"
            />
            <H6Emphasized>{group.name}</H6Emphasized>
            <BodySmallRegular color="$gray1000">
                {caseCountLabel(group.results.length)}
            </BodySmallRegular>
        </XStack>
        <YStack
            marginLeft={14}
            backgroundColor="white"
            borderColor="$gray400"
            borderWidth={1}
            borderRadius={16}
            overflow="hidden"
        >
            {group.results.map((result, index) => (
                <ResultCard
                    key={result.id}
                    result={result}
                    isFirst={index === 0}
                />
            ))}
        </YStack>
    </YStack>
);

const RunDetailHeader = ({
    agentId,
    run,
}: {
    agentId: string;
    run: ExperimentDetail | null;
}) => {
    const router = useRouter();
    const version =
        run == null ? null : versionLabel(run.summary.datasetVersion);
    const when =
        run == null ? '' : formatToSimpleDateTimeString(run.summary.startedAt);

    return (
        <YStack
            backgroundColor="white"
            borderColor="$gray200"
            borderWidth={1}
            borderRadius={20}
            padding={20}
            gap={12}
        >
            <XStack
                justifyContent="space-between"
                alignItems="flex-start"
                gap={16}
            >
                <YStack flex={1} gap={8}>
                    <H5Emphasized>
                        {run?.summary.name ?? 'Experiment'}
                    </H5Emphasized>
                    {run != null ? (
                        <XStack gap={8} flexWrap="wrap" alignItems="center">
                            <BadgeLabel
                                variant={statusVariant(run.summary.status)}
                                size="regular"
                                text={statusLabel(run.summary.status)}
                            />
                            <BodySmallRegular color="$gray1000">
                                {[when, version]
                                    .filter(
                                        (part) => part != null && part !== '',
                                    )
                                    .join(' · ')}
                            </BodySmallRegular>
                        </XStack>
                    ) : null}
                </YStack>
                <XStack gap={8} alignItems="center">
                    {run != null ? (
                        <ExperimentActions
                            agentId={agentId}
                            experimentId={run.summary.id}
                            onDeleted={() => {
                                router.replace({
                                    pathname: '/dataset/[agentId]/experiments',
                                    params: { agentId },
                                });
                            }}
                        />
                    ) : null}
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.SMALL}
                        width="auto"
                        text="Experiments"
                        onPress={() => {
                            router.push({
                                pathname: '/dataset/[agentId]/experiments',
                                params: { agentId },
                            });
                        }}
                    />
                </XStack>
            </XStack>
            {run != null ? (
                <BodySmallRegular>{countsLabel(run.summary)}</BodySmallRegular>
            ) : null}
        </YStack>
    );
};

const LoadedRunDetail = ({ run }: { run: ExperimentDetail }) => {
    const [bracket, setBracket] = useState<string | null>(null);
    const results =
        bracket == null
            ? run.results
            : run.results.filter(
                  (result) => bracketKeyForResult(result) === bracket,
              );
    const groups = groupRunResults(results, run.groupOrder);

    return (
        <>
            <RubricSummary
                scores={judgeScoreValues(run.results)}
                totalItems={run.summary.totalItems}
                selectedBracket={bracket}
                onSelectBracket={setBracket}
            />
            {bracket != null && groups.length === 0 ? (
                <PageMessage>No cases in this bracket.</PageMessage>
            ) : (
                <YStack gap={32}>
                    {groups.map((group) => (
                        <RunGroup key={group.name} group={group} />
                    ))}
                </YStack>
            )}
        </>
    );
};

const RunDetailBody = ({
    agentId,
    run,
    isLoading,
    isError,
}: {
    agentId: string;
    run: ExperimentDetail | null;
    isLoading: boolean;
    isError: boolean;
}) => (
    <ScrollView flex={1}>
        <YStack gap={24} paddingBottom={24}>
            <RunDetailHeader agentId={agentId} run={run} />
            {isLoading ? <LoadingCard /> : null}
            {isError ? (
                <PageMessage>Unable to load this experiment.</PageMessage>
            ) : null}
            {!isLoading && !isError && run == null ? (
                <PageMessage>
                    This experiment is not on the published dataset.
                </PageMessage>
            ) : null}
            {!isLoading && !isError && run != null ? (
                <LoadedRunDetail key={run.summary.id} run={run} />
            ) : null}
        </YStack>
    </ScrollView>
);

export const ExperimentPage = ({
    agentId,
    experimentId,
}: {
    agentId: string;
    experimentId: string;
}) => {
    const { run, isLoading, isError } = useExperiment(agentId, experimentId);

    return (
        <YStack flex={1} minHeight={0} width="100%" height="100%" padding={16}>
            <RunDetailBody
                agentId={agentId}
                run={run}
                isLoading={isLoading}
                isError={isError}
            />
        </YStack>
    );
};
